import { StatisticConnector, StatisticOperator } from '../../enums/assetStatistic';
import type { StatisticCondition, StatisticConditionValue } from '../../types/InfrastructureManagerTypes';

// GeoServer CQL for backend-style conditions. Mirrors the Map Portal query tool's buildMatchCql (escaping and
// left-to-right AND/OR folding), so a statistic filters the map the way the query tool highlights it.
// BE: confirm IS_EMPTY / CONTAINS semantics match the statistic calculation.

const column = (field: string) => `"${field.replace(/"/g, '""')}"`;

const literal = (value: StatisticConditionValue) =>
  typeof value === 'number' || typeof value === 'boolean' ? String(value) : `'${String(value ?? '').replace(/'/g, "''")}'`;

const toCql = ({ field, operator, value }: StatisticCondition) => {
  const name = column(field);
  switch (operator) {
    case StatisticOperator.IS:
      return `${name} = ${literal(value)}`;
    case StatisticOperator.IS_NOT:
      return `${name} <> ${literal(value)}`;
    case StatisticOperator.CONTAINS:
      return `${name} ILIKE '%${String(value ?? '').replace(/'/g, "''")}%'`;
    case StatisticOperator.GT:
      return `${name} > ${literal(value)}`;
    case StatisticOperator.GTE:
      return `${name} >= ${literal(value)}`;
    case StatisticOperator.LT:
      return `${name} < ${literal(value)}`;
    case StatisticOperator.LTE:
      return `${name} <= ${literal(value)}`;
    case StatisticOperator.IS_EMPTY:
      return `${name} IS NULL`;
    case StatisticOperator.IS_NOT_EMPTY:
      return `${name} IS NOT NULL`;
    default:
      return null;
  }
};

// A AND B OR C = (A AND B) OR C, matching the backend's evaluation order.
export const buildConditionsCql = (conditions: StatisticCondition[]) =>
  conditions.reduce((result, condition) => {
    const cql = toCql(condition);
    if (!cql) return result;
    if (!result) return cql;
    return condition.connector === StatisticConnector.OR ? `(${result}) OR (${cql})` : `(${result}) AND (${cql})`;
  }, '');

// Case-insensitive "contains" on one field, the same rule the asset list search uses.
export const buildContainsCql = (field: string, term: string) =>
  `strToLowerCase(${column(field)}) LIKE '%${term.toLowerCase().replace(/'/g, "''")}%'`;

export const joinCql = (...parts: string[]) => {
  const present = parts.filter(Boolean);
  return present.length > 1 ? present.map((part) => `(${part})`).join(' AND ') : (present[0] ?? '');
};
